import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Mail, LockKeyhole, ShieldCheck, UserRound, UserRoundPlus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { resolveAuthenticatedHomePath } from "../lib/roleAwareRouting";

/**
 * frontend/src/pages/Auth.jsx
 *
 * Context-aware auth flow
 * - Preserves the centered auth layout and routing behavior
 * - Keeps the Sign In / Create Account toggle in the existing page
 * - Preserves password reset flow
 * - Replaces vague auth failures with plain-English explanations
 * - Adds pricing-plan context so account creation feels like part of subscribing
 *
 * © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
 */

const DISALLOWED_POST_AUTH_PATHS = new Set(["/subscription", "/auth", "/auth/callback"]);

function getExplicitNextFromSearch(search) {
  try {
    const params = new URLSearchParams(search || "");
    const raw = params.get("next");
    if (!raw) return "";

    const decoded = decodeURIComponent(raw);
    if (typeof decoded !== "string") return "";
    if (!decoded.startsWith("/")) return "";
    if (decoded.startsWith("//")) return "";

    const pathnameOnly = decoded.split("?")[0].replace(/\/+$/g, "") || "/";
    if (DISALLOWED_POST_AUTH_PATHS.has(pathnameOnly)) return "";

    return decoded;
  } catch {
    return "";
  }
}

function getPlanContextFromSearch(search) {
  try {
    const params = new URLSearchParams(search || "");
    const planKey = String(params.get("plan") || "").trim();
    const planName = String(params.get("plan_name") || "").trim();
    const planPriceRaw = String(params.get("plan_price") || "").trim();
    const planBilling = String(params.get("plan_billing") || "").trim();
    const cents = Number(planPriceRaw);

    return {
      planKey,
      planName,
      planPriceCents: Number.isFinite(cents) ? cents : null,
      planBilling,
      hasPlanContext: Boolean(planKey || planName),
    };
  } catch {
    return {
      planKey: "",
      planName: "",
      planPriceCents: null,
      planBilling: "",
      hasPlanContext: false,
    };
  }
}

function formatPlanPrice(cents, billingLabel) {
  const value = Number(cents);
  const money = Number.isFinite(value)
    ? `$${(value / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`
    : "";
  return `${money}${billingLabel || ""}`.trim();
}

function getModeFromSearch(search, hasPlanContext) {
  try {
    const params = new URLSearchParams(search || "");
    const raw = String(params.get("mode") || "").trim().toLowerCase();
    if (raw === "signup") return "signup";
    if (raw === "signin") return "signin";
    return hasPlanContext ? "signup" : "signin";
  } catch {
    return hasPlanContext ? "signup" : "signin";
  }
}

function hasExplicitModeFromSearch(search) {
  try {
    const raw = String(new URLSearchParams(search || "").get("mode") || "")
      .trim()
      .toLowerCase();
    return raw === "signup" || raw === "signin";
  } catch {
    return false;
  }
}

function getIntentFromSearch(search, explicitNextPath) {
  try {
    const raw = String(new URLSearchParams(search || "").get("intent") || "")
      .trim()
      .toLowerCase();
    if (raw === "affiliate") return "affiliate";
    if (raw === "subscriber") return "subscriber";
  } catch {
    // fall through to route-derived intent
  }

  return String(explicitNextPath || "").startsWith("/sales-affiliate/")
    ? "affiliate"
    : "subscriber";
}

function getResetRedirectUrl() {
  try {
    if (typeof window === "undefined") return undefined;
    return `${window.location.origin}/reset-password`;
  } catch {
    return undefined;
  }
}

function stringifyError(error) {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function mapAuthErrorToMessage(error, mode) {
  const raw = stringifyError(error);
  const lower = raw.trim().toLowerCase();

  if (!lower) {
    return mode === "signup"
      ? "We could not create your account right now. Please try again in a moment."
      : "We could not sign you in right now. Please try again in a moment.";
  }

  if (lower.includes("email not confirmed") || lower.includes("confirm your email") || lower.includes("confirmation")) {
    return "Your account has been created, but your email has not been confirmed yet. Please check your inbox and click the confirmation link before signing in.";
  }

  if (
    lower.includes("user already registered") ||
    lower.includes("already registered") ||
    lower.includes("already exists") ||
    lower.includes("already been registered")
  ) {
    return "An account with this email already exists. Try Sign In instead, or use Forgot password? if you need a reset link.";
  }

  if (
    lower.includes("invalid login credentials") ||
    lower.includes("invalid grant") ||
    lower.includes("grant_type=password") ||
    lower.includes("body stream already read")
  ) {
    return "We could not sign you in. Please check your email and password. If this is a new account, confirm your email first and then try again.";
  }

  if (lower.includes("password should be at least") || (lower.includes("password") && lower.includes("6"))) {
    return "Please use a password with at least 6 characters.";
  }

  if (lower.includes("network") || lower.includes("fetch") || lower.includes("failed to fetch")) {
    return "We could not reach the sign-in service right now. Please check your connection and try again in a moment.";
  }

  if (lower.includes("signup is disabled")) {
    return "Account creation is temporarily unavailable. Please try again later.";
  }

  return mode === "signup"
    ? "We could not create your account right now. Please check your details and try again."
    : "We could not sign you in. Please check your email and password and try again.";
}

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();

  const planContext = useMemo(() => getPlanContextFromSearch(location.search), [location.search]);
  const explicitNextPath = useMemo(() => getExplicitNextFromSearch(location.search), [location.search]);
  const intent = useMemo(
    () => getIntentFromSearch(location.search, explicitNextPath),
    [location.search, explicitNextPath]
  );
  const explicitMode = useMemo(() => hasExplicitModeFromSearch(location.search), [location.search]);
  const nextPath = explicitNextPath || "/dashboard";
  const defaultMode = useMemo(
    () => getModeFromSearch(location.search, planContext.hasPlanContext),
    [location.search, planContext.hasPlanContext]
  );

  const [mode, setMode] = useState(defaultMode);
  const [stage, setStage] = useState(
    planContext.hasPlanContext || explicitMode ? "form" : "choice"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const redirectedRef = useRef(false);
  const isCreateMode = mode === "signup";
  const isAffiliateIntent = intent === "affiliate";
  const planDisplayName = planContext.planName || planContext.planKey || "Selected plan";
  const planDisplayPrice = planContext.hasPlanContext
    ? formatPlanPrice(planContext.planPriceCents, planContext.planBilling)
    : "";
  const planSummary = [planDisplayName, planDisplayPrice].filter(Boolean).join(" — ");

  useEffect(() => {
    setMode(defaultMode);
    setStage(planContext.hasPlanContext || explicitMode ? "form" : "choice");
    setErrorMsg("");
    setSuccessMsg("");
    if (defaultMode !== "signup") {
      setMarketingOptIn(false);
    }
  }, [defaultMode, explicitMode, intent, planContext.hasPlanContext]);

  const goNext = useCallback(async () => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;

    let destination = explicitNextPath;

    if (!destination) {
      destination = intent === "subscriber"
        ? "/dashboard"
        : await resolveAuthenticatedHomePath({
            client: supabase,
            fallbackPath: "/dashboard",
          });
    }

    navigate(destination, { replace: true });
  }, [explicitNextPath, intent, navigate]);

  const chooseMode = (nextMode) => {
    setMode(nextMode);
    setStage("form");
    setErrorMsg("");
    setSuccessMsg("");
    if (nextMode !== "signup") {
      setMarketingOptIn(false);
    }
  };

  const returnToChoice = () => {
    setStage("choice");
    setErrorMsg("");
    setSuccessMsg("");
    setPassword("");
    setMarketingOptIn(false);
  };

  useEffect(() => {
    let mounted = true;
    redirectedRef.current = false;
    const shouldAutoRedirect = stage !== "choice";

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        if (data?.session && shouldAutoRedirect) goNext();
      } catch {
        // no-op; user can proceed manually
      }
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session && shouldAutoRedirect) goNext();
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe?.();
    };
  }, [goNext, stage]);

  async function handleAuth(e) {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    setBusy(true);

    try {
      const cleanEmail = String(email || "").trim();
      const cleanPw = String(password || "");

      if (!cleanEmail || !cleanPw) {
        setErrorMsg("Please enter both your email and password.");
        return;
      }

      if (isCreateMode) {
        const subscriberSignupNext = !isAffiliateIntent && !planContext.hasPlanContext
          ? "/pricing"
          : "";
        const confirmationNext = explicitNextPath || subscriberSignupNext;
        const confirmationParams = new URLSearchParams();
        confirmationParams.set("intent", intent);
        confirmationParams.set("mode", "signin");
        if (confirmationNext) {
          confirmationParams.set("next", confirmationNext);
        }

        const emailRedirectTo = typeof window !== "undefined"
          ? `${window.location.origin}/auth?${confirmationParams.toString()}`
          : undefined;

        const consentCapturedAt = new Date().toISOString();

        const { data, error } = await supabase.auth.signUp({
          email: cleanEmail,
          password: cleanPw,
          options: {
            emailRedirectTo,
            data: {
              marketing_opt_in: Boolean(marketingOptIn),
              marketing_opt_in_source: marketingOptIn ? "signup" : null,
              marketing_opt_in_at: marketingOptIn ? consentCapturedAt : null,
            },
          },
        });

        if (error) {
          console.error("Auth signup failed", error);
          setErrorMsg(mapAuthErrorToMessage(error, "signup"));
          return;
        }

        const hasSession = Boolean(data?.session);
        const identities = Array.isArray(data?.user?.identities) ? data.user.identities : [];
        const likelyExistingUser = Boolean(data?.user && identities.length === 0);

        if (hasSession) {
          setSuccessMsg("Account created successfully. Redirecting you now.");
          goNext();
          return;
        }

        if (likelyExistingUser) {
          setErrorMsg("An account with this email may already exist. Try Sign In instead, or use Forgot password? if you need a reset link.");
          setMarketingOptIn(false);
          setMode("signin");
          return;
        }

        setSuccessMsg(
          isAffiliateIntent
            ? "Account created. Please check your email, confirm your address, then sign in to continue your Sales Affiliate application."
            : planContext.hasPlanContext
            ? "Account created. Please check your email, confirm your address, then sign in to continue with your selected plan."
            : "Account created. Please check your email, confirm your address, then sign in to continue to plans and subscription setup."
        );
        setPassword("");
        setMarketingOptIn(false);
        setMode("signin");
        return;
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password: cleanPw,
      });

      if (error) {
        console.error("Auth sign-in failed", error);
        setErrorMsg(mapAuthErrorToMessage(error, "signin"));
        return;
      }
    } catch (err) {
      console.error("Auth request crashed", err);
      setErrorMsg(mapAuthErrorToMessage(err, isCreateMode ? "signup" : "signin"));
    } finally {
      setBusy(false);
    }
  }

  async function handleForgotPassword() {
    setErrorMsg("");
    setSuccessMsg("");

    const cleanEmail = String(email || "").trim();
    if (!cleanEmail) {
      setErrorMsg("Enter your email above first, then select Forgot password?.");
      return;
    }

    setResetBusy(true);

    try {
      const redirectTo = getResetRedirectUrl();
      const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
        redirectTo,
      });

      if (error) {
        console.error("Password reset request failed", error);
        setErrorMsg("We could not send a password reset email right now. Please try again in a moment.");
        return;
      }

      setSuccessMsg(
        "Password reset email sent. Check your inbox and follow the secure link to create a new password."
      );
    } catch (err) {
      console.error("Password reset request crashed", err);
      setErrorMsg("We could not send a password reset email right now. Please try again in a moment.");
    } finally {
      setResetBusy(false);
    }
  }

  const accent = isAffiliateIntent
    ? {
        soft: "border-blue-200 bg-blue-50",
        icon: "border-blue-200 bg-blue-100 text-blue-700",
        button: "bg-blue-600 hover:bg-blue-700",
        outline: "border-blue-300 text-blue-700 hover:bg-blue-50",
        text: "text-blue-700",
      }
    : {
        soft: "border-slate-300 bg-white",
        icon: "border-slate-200 bg-slate-100 text-slate-700",
        button: "bg-slate-900 hover:bg-slate-800",
        outline: "border-slate-300 text-slate-800 hover:bg-slate-50",
        text: "text-slate-700",
      };

  if (stage === "choice") {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <div className="mx-auto flex min-h-screen max-w-[1500px] items-center justify-center px-6 py-10 sm:px-8 lg:px-12 lg:py-14">
          <div className="w-full max-w-5xl">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm sm:p-9 lg:p-10">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.26em] text-slate-500">
                    AI Fine Protector™
                  </div>
                  <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-[2.15rem]">
                    {isAffiliateIntent ? "Sales Affiliate Access" : "Subscriber Access"}
                  </h1>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Choose how you want to continue.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Back to Landing Page
                </button>
              </div>

              <div className="mt-8 grid gap-5 md:grid-cols-2">
                <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700">
                    <UserRound size={24} />
                  </div>
                  <h2 className="mt-5 text-xl font-bold text-slate-900">
                    I already have an AFP account
                  </h2>
                  <p className="mt-3 min-h-[72px] text-sm leading-6 text-slate-600">
                    {isAffiliateIntent
                      ? "Sign in to continue your Sales Affiliate application or access your affiliate workspace."
                      : "Sign in to access your subscriber dashboard, reports, remediation, and AI News."}
                  </p>
                  <button
                    type="button"
                    onClick={() => chooseMode("signin")}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Sign In
                    <ArrowRight size={17} />
                  </button>
                </div>

                <div className={`rounded-[1.75rem] border p-6 ${accent.soft}`}>
                  <div className={`flex h-12 w-12 items-center justify-center rounded-full border ${accent.icon}`}>
                    <UserRoundPlus size={24} />
                  </div>
                  <h2 className="mt-5 text-xl font-bold text-slate-900">I'm new to AFP</h2>
                  <p className="mt-3 min-h-[72px] text-sm leading-6 text-slate-600">
                    {isAffiliateIntent
                      ? "Create your AFP account to begin the Sales Affiliate application and onboarding process."
                      : "Create your AFP account and continue to plans and subscription setup."}
                  </p>
                  <button
                    type="button"
                    onClick={() => chooseMode("signup")}
                    className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border bg-white px-5 py-3.5 text-sm font-semibold transition ${accent.outline}`}
                  >
                    Create Account
                    <ArrowRight size={17} />
                  </button>
                </div>
              </div>

              <div className="mt-7 flex flex-col items-center justify-between gap-4 border-t border-slate-200 pt-6 sm:flex-row">
                <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                  <ShieldCheck size={16} className={accent.text} />
                  Secure AFP account access
                </div>
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      isAffiliateIntent
                        ? "/auth?intent=subscriber"
                        : "/auth?intent=affiliate&next=%2Fsales-affiliate%2Fapplication"
                    )
                  }
                  className="text-sm font-semibold text-blue-700 transition hover:underline"
                >
                  {isAffiliateIntent ? "Switch to Subscriber Access" : "Switch to Sales Affiliate Access"}
                </button>
              </div>

              <div className="mt-7 text-center text-xs text-slate-500">
                © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formTitle = planContext.hasPlanContext
    ? isCreateMode
      ? "Create your account to continue with your subscription"
      : "Sign in to continue with your subscription"
    : isAffiliateIntent
    ? isCreateMode
      ? "Create your AFP account"
      : "Sign in for Sales Affiliate Access"
    : isCreateMode
    ? "Create your AI Fine Protector™ account"
    : "Sign in to your AFP account";

  const formDescription = planContext.hasPlanContext
    ? isCreateMode
      ? "Create secure credentials now. After confirmation, you will continue with your selected plan."
      : "Use your existing credentials to continue with the plan you selected."
    : isAffiliateIntent
    ? isCreateMode
      ? "Create secure credentials, confirm your email, and continue to the Sales Affiliate Application."
      : "Use your existing AFP credentials to continue your Sales Affiliate application or workspace."
    : isCreateMode
    ? "Create secure credentials, confirm your email, and continue to plans and subscription setup."
    : "Use your existing AFP credentials to access your subscriber account.";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto flex min-h-screen max-w-[1600px] items-center justify-center px-6 py-10 sm:px-8 lg:px-12 lg:py-14">
        <div className="w-full max-w-xl">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm lg:p-10">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.26em] text-slate-500">
                  AI Fine Protector™
                </div>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-[2rem]">
                  {formTitle}
                </h1>
                <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
                  {formDescription}
                </p>
              </div>

              <button
                type="button"
                onClick={() => planContext.hasPlanContext ? navigate(nextPath) : returnToChoice()}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                {planContext.hasPlanContext ? "Back to Pricing" : "Back to Access Choices"}
              </button>
            </div>

            {planContext.hasPlanContext ? (
              <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-950 shadow-sm">
                <div className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">Selected plan</div>
                <div className="mt-2 font-bold">{planSummary}</div>
                <div className="mt-1 text-blue-900/80">
                  {isCreateMode
                    ? "Create your account here, then continue with this plan."
                    : "Sign in here, then continue with this plan."}
                </div>
              </div>
            ) : null}

            {errorMsg ? (
              <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm leading-6 text-red-700">
                {errorMsg}
              </div>
            ) : null}

            {successMsg ? (
              <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm leading-6 text-emerald-800">
                {successMsg}
              </div>
            ) : null}

            <form onSubmit={handleAuth} className="mt-8 space-y-6">
              <div>
                <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                  Email
                </label>
                <div className="relative">
                  <Mail
                    size={18}
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    autoComplete="email"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-4 pl-12 pr-4 text-sm font-medium text-slate-900 outline-none transition focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-4">
                  <label className="block text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                    Password
                  </label>

                  {!isCreateMode ? (
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={resetBusy || busy}
                      className="text-xs font-semibold text-slate-600 transition hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {resetBusy ? "Sending..." : "Forgot password?"}
                    </button>
                  ) : (
                    <div className="text-xs font-medium text-slate-500">Minimum 6 characters</div>
                  )}
                </div>
                <div className="relative">
                  <LockKeyhole
                    size={18}
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    required
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete={isCreateMode ? "new-password" : "current-password"}
                    minLength={6}
                    className="w-full rounded-2xl border border-slate-200 bg-white py-4 pl-12 pr-4 text-sm font-medium text-slate-900 outline-none transition focus:border-blue-500"
                  />
                </div>
              </div>

              {isCreateMode ? (
                <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={marketingOptIn}
                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                  />
                  <span>
                    I’d like to receive product updates, insights, and occasional marketing communications.
                  </span>
                </label>
              ) : null}

              <button
                type="submit"
                disabled={busy || resetBusy}
                className={`inline-flex w-full items-center justify-center gap-3 rounded-2xl px-5 py-4 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${accent.button}`}
              >
                {busy ? "Working..." : isCreateMode ? "Create Account" : "Sign In"}
                <ArrowRight size={18} />
              </button>
            </form>

            <div className={`mt-7 rounded-2xl border px-5 py-4 text-sm leading-6 ${accent.soft}`}>
              <div className="flex items-start gap-3">
                <ShieldCheck size={18} className={`mt-0.5 shrink-0 ${accent.text}`} />
                <div className="text-slate-700">
                  {isCreateMode
                    ? isAffiliateIntent
                      ? "After creating your account, confirm your email and continue to the Sales Affiliate Application."
                      : planContext.hasPlanContext
                      ? "After creating your account, confirm your email and continue with your selected subscription plan."
                      : "After creating your account, confirm your email and continue to plans and subscription setup."
                    : isAffiliateIntent
                    ? "Sign in with your existing AFP account to continue your Sales Affiliate path."
                    : "Sign in with your existing AFP account to continue your subscriber path."}
                </div>
              </div>
            </div>

            <div className="mt-7 flex items-center justify-between gap-4 border-t border-slate-200 pt-5">
              <button
                type="button"
                onClick={() => chooseMode(isCreateMode ? "signin" : "signup")}
                className={`text-sm font-semibold transition hover:underline ${accent.text}`}
              >
                {isCreateMode ? "Already have an account? Sign In" : "New to AFP? Create Account"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="text-sm font-semibold text-slate-500 transition hover:text-slate-800"
              >
                Landing Page
              </button>
            </div>

            <div className="mt-8 text-center text-xs text-slate-500">
              © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
